/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {useMemo, useRef} from 'react';
import {scaleLinear, scaleTime} from '@visx/scale';
import {Brush} from '@visx/brush';
import {Bounds} from '@visx/brush/lib/types';
import BaseBrush from '@visx/brush/lib/BaseBrush';
import {extent, max} from '@visx/vendor/d3-array';
import AreaChart from './AreaChart';
import {CallStatisticsMetricsItem} from './CallStatistics.utils';

const brushMargin = { top: 0, bottom: 0, left: 60, right: 0 };
const chartSeparation = 0;
export const accentColor = '#f6acc8';
export const background = '#584153';
export const background2 = '#af8baf';

export type BrushProps = {
  width: number;
  height: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  compact?: boolean;
};

export type CallStatisticsTimeLapChartProps = {
  width: number;
  height: number;
  data: CallStatisticsMetricsItem[];
  setFilteredData: (filtered: CallStatisticsMetricsItem[]) => void;
  outgoingShown: boolean;
  incomingShown: boolean;
  forwardedShown: boolean;
};

const getDate = (d: CallStatisticsMetricsItem) => d.date;

function CallStatisticsTimeLapChart({
  width,
  height,
  data,
  setFilteredData,
  outgoingShown,
  incomingShown,
  forwardedShown
}: CallStatisticsTimeLapChartProps) {
  const brushRef = useRef<BaseBrush | null>(null);

  const onBrushChange = (domain: Bounds | null) => {
    if (!domain) return;
    const { x0, x1 } = domain;
    const stockCopy = data.filter((s) => {
      const x = getDate(s).getTime();
      return x > x0 && x < x1;
    });
    setFilteredData?.(stockCopy);
  };

  const innerHeight = height - 18;
  const topChartBottomMargin = 0; 
  const topChartHeight = 0;
  const bottomChartHeight = innerHeight - topChartHeight - chartSeparation;

  const xBrushMax = Math.max(width - brushMargin.left - brushMargin.right, 0);
  const yBrushMax = Math.max(bottomChartHeight - brushMargin.top - brushMargin.bottom, 0);

  const brushDateScale = useMemo(
    () =>
      scaleTime<number>({
        range: [0, xBrushMax],
        domain: extent(data, getDate) as [Date, Date],
      }),
    [xBrushMax],
  );
  const brushStockScale = useMemo(
    () => {
      let yMax = max(data, (d) => Math.max(
        (incomingShown ? d.incoming : 0), 
        (outgoingShown ? d.outgoing : 0), 
        (forwardedShown ? d.forwarded : 0)
      )) || 0;
      yMax = Math.max(yMax, 15); //15 seconds is min for chart
      return scaleLinear({
        range: [yBrushMax, 0],
        domain: [0, yMax],
        nice: true,
      });
    },
    [data, yBrushMax, incomingShown, outgoingShown, forwardedShown],
  );

  const initialBrushPosition = useMemo(() => {
      let dataLength = Math.trunc(data.length * 0.25); // 25% as default
      const minLength = Math.trunc(data.length * 0.05); // 5% of data

      if(dataLength > 10) {
        dataLength = minLength;
      }
      if(dataLength % 2 != 0) {
        dataLength++;
      }
      let startDayIndx = data.length - dataLength;
      let finishDayIndex = data.length - 1;
      let maxDataIndex = 0;
      for(let di=0; di < data.length; di++) {
        const totalCalls = (incomingShown ? data[di].incoming : 0) 
          + (outgoingShown ? data[di].outgoing : 0)
          + (forwardedShown ? data[di].forwarded : 0)
          ;
        const maxCalls = (incomingShown ? data[maxDataIndex].incoming : 0) 
          + (outgoingShown ? data[maxDataIndex].outgoing : 0)
          + (forwardedShown ? data[maxDataIndex].forwarded : 0)
          ;
        if(totalCalls >= maxCalls) {
          maxDataIndex = di;
        }
      }

      if((maxDataIndex - (dataLength / 2)) < 0)
      {
        startDayIndx = 0;
        finishDayIndex = dataLength;
      }
      else if((maxDataIndex + (dataLength / 2)) >= data.length)
      {
        startDayIndx = data.length - dataLength;
        finishDayIndex = data.length - 1;
      }
      else {
        startDayIndx = maxDataIndex - Math.trunc(dataLength / 2);
        finishDayIndex = startDayIndx + dataLength;
      }

      const returnValue = {
        start: { x: brushDateScale(getDate(data[startDayIndx])) },
        end: { x: brushDateScale(getDate(data[finishDayIndex])) },
      };

      const initFiltered = data.slice(startDayIndx, finishDayIndex + 1);
      setFilteredData?.(initFiltered);

      return returnValue;
    }, [brushDateScale, data, incomingShown, outgoingShown, forwardedShown],
  );

  return (
    <div>
      <svg width={width} height={height}>
        <AreaChart
          hideLeftAxis
          data={data}
          width={width}
          yMax={yBrushMax}
          xScale={brushDateScale}
          yScale={brushStockScale}
          margin={brushMargin}
          top={topChartHeight + topChartBottomMargin}
          gradientColor={background2}
          incomingShown={incomingShown}
          outgoingShown={outgoingShown}
          forwardedShown={forwardedShown}
        >
          <Brush
            xScale={brushDateScale}
            yScale={brushStockScale}
            width={xBrushMax}
            height={yBrushMax}
            margin={brushMargin}
            handleSize={8}
            innerRef={brushRef}
            resizeTriggerAreas={['left', 'right']}
            brushDirection="horizontal"
            initialBrushPosition={initialBrushPosition}
            onChange={onBrushChange}
            //onClick={() => setFilteredData?.(data)}
            useWindowMoveEvents
            renderBrushHandle={({ x, height }) => (
              <rect
                x={x - 6}
                y={0}
                width={12}
                height={height}
                fill="rgba(255, 255, 255, 0)"
                stroke="rgba(255, 255, 255, 0)"
                rx={2}
                style={{ cursor: 'ew-resize' }}
              />
            )}
            selectedBoxStyle={{
              fill: "rgba(81, 166, 237, 1)",
              fillOpacity: 0.4,
              stroke: "rgba(81, 166, 237, 1)",
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </svg>
    </div>
  );
}

export default CallStatisticsTimeLapChart;
